// 不通知區（docs/mute-zones.md §11，M2）：API、框的拖移 / 縮放（夾在圖內）、狀態文字判斷、名稱查詢。
// 家人在誤報截圖上圈出電視 / 螢幕那塊 → 偵測框整個落在裡面的事件只存不推播（payload.verify = "muted"）。
import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

export const MUTE_LABELS = ['person', 'animal', 'vehicle'] as const;
export type MuteLabel = (typeof MUTE_LABELS)[number];
export type Box = [number, number, number, number];

export interface MuteZone {
  zone_id: string;
  name: string;
  labels: MuteLabel[];
  box: Box;
  created_by: string;
  created_at: number;
  expires_at: number; // 0 = 一直有效
  hits: number;
  last_hit_at?: number;
  misses?: number[];
  disabled?: boolean;
  mine: boolean;
  expired: boolean;
}

export interface MuteZoneBody {
  name?: string;
  labels?: MuteLabel[];
  box?: Box;
  expires_days?: number; // 30 | -1
  from_event?: string;
}

type Ev = { payload?: Record<string, unknown>; has_snapshot?: boolean };

export function isMuted(e: Ev): boolean {
  return e.payload?.verify === 'muted';
}

/** 事件的物件種類（只有人 / 動物 / 車能設不通知區；pet 當動物） */
export function muteLabelOf(e: Ev): MuteLabel | null {
  const raw = String(e.payload?.label ?? e.payload?.detect_label ?? '').toLowerCase();
  const l = raw === 'pet' ? 'animal' : raw;
  return (MUTE_LABELS as readonly string[]).includes(l) ? (l as MuteLabel) : null;
}

/** 能不能按［這種不要再通知］：物件事件、有截圖、還不在不通知區 */
export function canMute(e: Ev): boolean {
  return muteLabelOf(e) !== null && e.has_snapshot === true && !isMuted(e);
}

export const MIN_SIDE = 0.02;
export const DEFAULT_BOX: Box = [0.375, 0.375, 0.625, 0.625];

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const r4 = (v: number) => Math.round(v * 10000) / 10000;

/** 整個框移動 dx, dy（0–1），不超出圖 */
export function moveBox(b: Box, dx: number, dy: number): Box {
  const w = b[2] - b[0];
  const h = b[3] - b[1];
  const x = clamp(b[0] + dx, 0, 1 - w);
  const y = clamp(b[1] + dy, 0, 1 - h);
  return [r4(x), r4(y), r4(x + w), r4(y + h)];
}

/** 拉右下角改大小（左上不動），最小 2% × 2%、不超出圖 */
export function resizeBox(b: Box, dx: number, dy: number): Box {
  const x2 = clamp(b[2] + dx, b[0] + MIN_SIDE, 1);
  const y2 = clamp(b[3] + dy, b[1] + MIN_SIDE, 1);
  return [b[0], b[1], r4(x2), r4(y2)];
}

export type ZoneStatus = { kind: 'off' } | { kind: 'expired' } | { kind: 'always' } | { kind: 'days'; days: number };

export function zoneStatus(z: Pick<MuteZone, 'disabled' | 'expires_at'>, nowSec: number): ZoneStatus {
  if (z.disabled) return { kind: 'off' };
  if (!z.expires_at) return { kind: 'always' };
  if (z.expires_at <= nowSec) return { kind: 'expired' };
  return { kind: 'days', days: Math.max(1, Math.ceil((z.expires_at - nowSec) / 86400)) };
}

const path = (deviceId: string) => `/v3/devices/${encodeURIComponent(deviceId)}/mute-zones`;

export async function listMuteZones(deviceId: string): Promise<MuteZone[]> {
  const r = await apiFetch<{ zones?: MuteZone[] }>(path(deviceId));
  return r.data?.zones ?? [];
}

export async function createMuteZone(deviceId: string, body: MuteZoneBody): Promise<MuteZone> {
  const r = await apiFetch<MuteZone>(path(deviceId), { method: 'POST', body });
  invalidate(deviceId);
  return r.data as MuteZone;
}

export async function updateMuteZone(deviceId: string, zoneId: string, body: MuteZoneBody): Promise<MuteZone> {
  const r = await apiFetch<MuteZone>(`${path(deviceId)}/${encodeURIComponent(zoneId)}`, { method: 'PATCH', body });
  invalidate(deviceId);
  return r.data as MuteZone;
}

export async function deleteMuteZone(deviceId: string, zoneId: string): Promise<void> {
  await apiFetch(`${path(deviceId)}/${encodeURIComponent(zoneId)}`, { method: 'DELETE' });
  invalidate(deviceId);
}

export async function reportMuteMiss(deviceId: string, zoneId: string, eventId: string): Promise<MuteZone> {
  const r = await apiFetch<MuteZone>(`${path(deviceId)}:missed`, { method: 'POST', body: { zone_id: zoneId, event_id: eventId } });
  invalidate(deviceId);
  return r.data as MuteZone;
}

// 名稱查詢：每台讀一次清單（同一頁多列共用）
const cache = new Map<string, Promise<MuteZone[]>>();
function invalidate(deviceId: string) {
  cache.delete(deviceId);
}
export function zonesOf(deviceId: string): Promise<MuteZone[]> {
  let p = cache.get(deviceId);
  if (!p) {
    p = listMuteZones(deviceId).catch(() => [] as MuteZone[]);
    cache.set(deviceId, p);
  }
  return p;
}

/** muted 事件的區名稱（找不到 → null，畫面顯示「不通知區」） */
export function useMuteZoneName(deviceId: string, zoneId: string | undefined): string | null {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    if (!zoneId) return;
    let alive = true;
    void zonesOf(deviceId).then((zs) => {
      if (alive) setName(zs.find((z) => z.zone_id === zoneId)?.name ?? null);
    });
    return () => {
      alive = false;
    };
  }, [deviceId, zoneId]);
  return name;
}
